/**
 * src/features/room/lib/createRoomFromTemplate.ts
 *
 * Pure functions (no React, no store reads) for starter template room creation.
 * Mirrors the factory.ts pattern for structural consistency.
 *
 * Consumed by:
 *   - useProjectStore.addRoomFromTemplate (store action)
 *   - test/room_template.test.ts (unit tests, TMPL-01)
 */

import { createRoom } from '@/entities/room/lib/factory';
import { generateId } from '@/shared/lib/id';
import type { StarterTemplate } from '@/entities/room/data/starterTemplates';
import type { Service } from '@/entities/service/model/types';
import type { RecipeLayer } from '@/entities/recipe/model/types';

/** RecipeCategoryMap shape from useRecipeStore — defined locally to avoid cross-store import */
type RecipeCategoryMap = Record<string, Array<{ id: string; layers: RecipeLayer[] }>>;

/**
 * Build a Room from a StarterTemplate.
 * - Uses the factory createRoom for consistent dim parsing (parseSafeNumber).
 * - items is always empty — services are resolved and added separately by the store action.
 * - Generates a fresh id on every call.
 */
export function buildRoomFromTemplate(template: StarterTemplate, translatedName: string) {
  return createRoom(generateId(), translatedName, {
    dims: template.dims,
    openings: template.openings ?? [],
    partitions: [],
    wallElements: [],
    items: [],
  });
}

/**
 * Resolve Service[] from recipe IDs.
 * - Flattens recipe layers across all categories in recipes map.
 * - Maps each layer.serviceId to a Service via serviceMap.
 * - Missing recipe IDs: log console.warn + contribute 0 services (no throw — Pitfall 5).
 * - Missing serviceId in serviceMap: filtered out silently.
 */
export function resolveServicesFromRecipeIds(
  recipeIds: string[],
  recipes: RecipeCategoryMap,
  serviceMap: Record<string, Service>,
): Service[] {
  return recipeIds
    .flatMap((id) => {
      const found = Object.values(recipes).flat().find((r) => r.id === id);
      if (!found) {
        console.warn(`[createRoomFromTemplate] recipe not found: ${id}`);
        return [] as RecipeLayer[];
      }
      return found.layers;
    })
    .map((layer) => serviceMap[layer.serviceId])
    .filter((s): s is Service => s !== undefined);
}
